import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Plus, Trash, PencilSimple, Globe, Trophy, Skull, Users, CaretDown, CaretUp, ArrowsClockwise } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamLogo, setTeamLogo] = useState('');
  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [matchDate, setMatchDate] = useState('');
  const [week, setWeek] = useState(1);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [loading, setLoading] = useState(true);

  // World Cup admin state
  const [wcTeams, setWcTeams] = useState([]);
  const [wcStatus, setWcStatus] = useState(null);
  const [wcForm, setWcForm] = useState({
    winner_team_id: '',
    runner_up_team_id: '',
    third_place_team_id: '',
    fourth_place_team_id: '',
    total_goals: 150,
  });
  const [wcSearch, setWcSearch] = useState('');

  // WC LMS Match state
  const [wcMatches, setWcMatches] = useState([]);
  const [wcMatchForm, setWcMatchForm] = useState({
    home_team_id: '',
    away_team_id: '',
    match_date: '',
    match_time: '15:00',
    stage: 'Group',
    group: '',
  });
  const [wcResultMatch, setWcResultMatch] = useState(null);
  const [wcMatchHomeScore, setWcMatchHomeScore] = useState(0);
  const [wcMatchAwayScore, setWcMatchAwayScore] = useState(0);
  const [wcMatchWinner, setWcMatchWinner] = useState('');

  // Admin Leagues state
  const [adminLeagues, setAdminLeagues] = useState([]);
  const [expandedLeague, setExpandedLeague] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, matchesRes, wcTeamsRes, wcStatusRes, wcMatchesRes, leaguesRes, syncStatusRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/matches`),
        axios.get(`${API}/wc/teams`),
        axios.get(`${API}/wc/status`),
        axios.get(`${API}/wc-lms/matches`),
        axios.get(`${API}/admin/leagues`),
        axios.get(`${API}/sync-status`),
      ]);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
      setWcTeams(wcTeamsRes.data);
      setWcStatus(wcStatusRes.data);
      setWcMatches(wcMatchesRes.data);
      setAdminLeagues(leaguesRes.data);
      setSyncStatus(syncStatusRes.data);

      // Load existing WC results if any
      if (wcStatusRes.data.results_entered) {
        try {
          const lbRes = await axios.get(`${API}/wc/leaderboard`);
          if (lbRes.data.results_entered && lbRes.data.leaderboard.length > 0) {
            // We don't have a direct get-results endpoint, but status tells us
          }
        } catch {}
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error('Please enter team name');
      return;
    }

    try {
      await axios.post(`${API}/admin/teams`, {
        name: teamName,
        logo: teamLogo || null,
      });
      toast.success('Team created!');
      setTeamName('');
      setTeamLogo('');
      setShowTeamModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await axios.delete(`${API}/admin/teams/${teamId}`);
      toast.success('Team deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleCreateMatch = async () => {
    if (!homeTeamId || !awayTeamId || !matchDate || !week) {
      toast.error('Please fill all fields');
      return;
    }

    if (homeTeamId === awayTeamId) {
      toast.error('Teams must be different');
      return;
    }

    try {
      await axios.post(`${API}/admin/matches`, {
        home_team_id: homeTeamId,
        away_team_id: awayTeamId,
        match_date: new Date(matchDate).toISOString(),
        week: parseInt(week),
      });
      toast.success('Match created!');
      setHomeTeamId('');
      setAwayTeamId('');
      setMatchDate('');
      setWeek(1);
      setShowMatchModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create match');
    }
  };

  const handleUpdateResult = async () => {
    if (!selectedMatch) return;

    try {
      await axios.put(`${API}/admin/matches/${selectedMatch.id}`, {
        home_score: parseInt(homeScore),
        away_score: parseInt(awayScore),
        status: 'completed',
      });
      toast.success('Result updated!');
      setSelectedMatch(null);
      setHomeScore(0);
      setAwayScore(0);
      setShowResultModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update result');
    }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await axios.delete(`${API}/admin/matches/${matchId}`);
      toast.success('Match deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete match');
    }
  };

  const handleSaveWcResults = async () => {
    if (!wcForm.winner_team_id || !wcForm.runner_up_team_id || !wcForm.third_place_team_id || !wcForm.fourth_place_team_id) {
      toast.error('Please select all 4 positions');
      return;
    }
    const ids = [wcForm.winner_team_id, wcForm.runner_up_team_id, wcForm.third_place_team_id, wcForm.fourth_place_team_id];
    if (new Set(ids).size !== 4) {
      toast.error('Must be 4 different teams');
      return;
    }
    try {
      await axios.put(`${API}/admin/wc/results`, wcForm);
      toast.success('World Cup results saved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save results');
    }
  };

  const handleCreateWcMatch = async () => {
    if (!wcMatchForm.home_team_id || !wcMatchForm.away_team_id || !wcMatchForm.match_date) {
      toast.error('Fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/wc-lms/match`, wcMatchForm);
      toast.success('WC match created!');
      setWcMatchForm({ home_team_id: '', away_team_id: '', match_date: '', match_time: '15:00', stage: 'Group', group: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create match');
    }
  };

  const handleSetWcMatchResult = async (matchId) => {
    try {
      await axios.put(`${API}/admin/wc-lms/match/${matchId}/result`, {
        home_score: wcMatchHomeScore,
        away_score: wcMatchAwayScore,
        winner_team_id: wcMatchWinner || null,
      });
      toast.success('Match result saved and picks processed!');
      setWcResultMatch(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save result');
    }
  };

  const handleDeleteWcMatch = async (matchId) => {
    if (!window.confirm('Delete this WC match?')) return;
    try {
      await axios.delete(`${API}/admin/wc-lms/match/${matchId}`);
      toast.success('Match deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete match');
    }
  };

  const handleDeleteLeague = async (leagueId, leagueName) => {
    if (!window.confirm(t('admin.confirmDeleteLeague', { name: leagueName }))) return;
    try {
      await axios.delete(`${API}/admin/leagues/${leagueId}`);
      toast.success(t('admin.leagueDeleted'));
      setExpandedLeague(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete league');
    }
  };

  const handleRemoveMember = async (leagueId, memberId, memberEmail) => {
    if (!window.confirm(t('admin.confirmRemoveMember', { email: memberEmail }))) return;
    try {
      await axios.delete(`${API}/admin/leagues/${leagueId}/members/${memberId}`);
      toast.success(t('admin.memberRemoved'));
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleSyncResults = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/sync-results`);
      const synced = res.data.synced || 0;
      if (synced > 0) {
        toast.success(t('admin.syncSuccess', { count: synced }));
      } else {
        toast.info(t('admin.syncUpToDate'));
      }
      fetchData();
    } catch (error) {
      toast.error(t('admin.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-text-secondary">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center space-x-4 mb-8">
          <Shield size={48} weight="bold" className="text-accent" />
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-white" data-testid="admin-title">
            {t('admin.title')}
          </h1>
        </div>

        <Tabs defaultValue="matches" className="w-full">
          <TabsList className="bg-background-paper border border-border rounded-sm p-1 mb-6">
            <TabsTrigger value="matches" data-testid="tab-matches" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              {t('admin.matches')}
            </TabsTrigger>
            <TabsTrigger value="teams" data-testid="tab-teams" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              {t('admin.teams')}
            </TabsTrigger>
            <TabsTrigger value="worldcup" data-testid="tab-worldcup" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              WC Prediction
            </TabsTrigger>
            <TabsTrigger value="wcmatches" data-testid="tab-wcmatches" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              WC Matches
            </TabsTrigger>
            <TabsTrigger value="leagues" data-testid="tab-admin-leagues" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              {t('admin.leagues')}
            </TabsTrigger>
          </TabsList>

          {/* Matches Tab */}
          <TabsContent value="matches" data-testid="matches-section">
            <div className="mb-4">
              <button
                onClick={() => setShowMatchModal(true)}
                data-testid="add-match-button"
                className="flex items-center space-x-2 px-4 py-2 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all hover:scale-105 glow-effect"
              >
                <Plus size={20} weight="bold" />
                <span>{t('admin.addMatch')}</span>
              </button>
            </div>

            <div className="space-y-3">
              {matches.map((match) => (
                <div
                  key={match.id}
                  data-testid={`admin-match-${match.id}`}
                  className="bg-background-paper border border-border rounded-sm p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <span className="text-xs font-mono text-text-muted">Week {match.week}</span>
                      <span className={`text-xs uppercase tracking-wide px-3 py-1 rounded-sm ${
                        match.status === 'completed' ? 'bg-primary/20 text-primary' : 'bg-background-subtle text-text-secondary'
                      }`}>
                        {match.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {match.status === 'scheduled' && (
                        <button
                          onClick={() => {
                            setSelectedMatch(match);
                            setHomeScore(0);
                            setAwayScore(0);
                            setShowResultModal(true);
                          }}
                          data-testid={`update-result-${match.id}`}
                          className="p-2 text-primary hover:bg-primary/10 rounded-sm transition-all"
                        >
                          <PencilSimple size={20} weight="bold" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteMatch(match.id)}
                        data-testid={`delete-match-${match.id}`}
                        className="p-2 text-accent hover:bg-accent/10 rounded-sm transition-all"
                      >
                        <Trash size={20} weight="bold" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-4 items-center">
                    <div className="col-span-3">
                      <p className="font-heading text-xl font-bold text-white">{getTeamName(match.home_team_id)}</p>
                    </div>
                    <div className="col-span-1 text-center">
                      {match.status === 'completed' ? (
                        <div className="flex items-center justify-center space-x-2">
                          <span className="font-mono text-2xl font-bold text-primary">{match.home_score}</span>
                          <span className="text-text-muted">-</span>
                          <span className="font-mono text-2xl font-bold text-primary">{match.away_score}</span>
                        </div>
                      ) : (
                        <span className="text-text-muted">VS</span>
                      )}
                    </div>
                    <div className="col-span-3 text-right">
                      <p className="font-heading text-xl font-bold text-white">{getTeamName(match.away_team_id)}</p>
                    </div>
                  </div>

                  <p className="text-xs text-text-muted mt-4">
                    {new Date(match.match_date).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams" data-testid="teams-section">
            <div className="mb-4">
              <button
                onClick={() => setShowTeamModal(true)}
                data-testid="add-team-button"
                className="flex items-center space-x-2 px-4 py-2 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all hover:scale-105 glow-effect"
              >
                <Plus size={20} weight="bold" />
                <span>{t('admin.addTeam')}</span>
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.map((team) => (
                <div
                  key={team.id}
                  data-testid={`admin-team-${team.id}`}
                  className="bg-background-paper border border-border rounded-sm p-6 flex items-center justify-between hover:border-primary/50 transition-all"
                >
                  <p className="font-heading text-xl font-bold text-white">{team.name}</p>
                  <button
                    onClick={() => handleDeleteTeam(team.id)}
                    data-testid={`delete-team-${team.id}`}
                    className="p-2 text-accent hover:bg-accent/10 rounded-sm transition-all"
                  >
                    <Trash size={20} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* World Cup Results Tab */}
          <TabsContent value="worldcup" data-testid="worldcup-section">
            <div className="bg-background-paper border border-border rounded-sm p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Globe size={28} weight="bold" className="text-[#FFD700]" />
                <h2 className="font-heading text-2xl font-bold text-white">World Cup 2026 Results</h2>
              </div>
              <p className="text-text-secondary text-sm mb-6">
                Enter the actual World Cup results. This will calculate the leaderboard for all user predictions.
                {wcStatus?.total_predictions > 0 && (
                  <span className="ml-2 text-primary font-mono">({wcStatus.total_predictions} predictions submitted)</span>
                )}
              </p>

              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  value={wcSearch}
                  onChange={e => setWcSearch(e.target.value)}
                  placeholder="Search teams..."
                  className="w-full max-w-sm px-4 py-2 bg-background border border-border rounded-sm text-white text-sm placeholder:text-text-muted focus:border-primary outline-none"
                  data-testid="wc-admin-search"
                />
              </div>

              {/* Position selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {[
                  { key: 'winner_team_id', label: 'Winner', icon: '1st' },
                  { key: 'runner_up_team_id', label: 'Runner-up', icon: '2nd' },
                  { key: 'third_place_team_id', label: '3rd Place', icon: '3rd' },
                  { key: 'fourth_place_team_id', label: '4th Place', icon: '4th' },
                ].map(({ key, label, icon }) => {
                  const selectedIds = [wcForm.winner_team_id, wcForm.runner_up_team_id, wcForm.third_place_team_id, wcForm.fourth_place_team_id].filter(Boolean);
                  const filteredTeams = wcTeams.filter(t => {
                    if (wcSearch && !t.name.toLowerCase().includes(wcSearch.toLowerCase())) return false;
                    return true;
                  });
                  return (
                    <div key={key} className="bg-background-subtle border border-border rounded-sm p-4">
                      <label className="block text-sm uppercase tracking-wide text-[#FFD700] font-bold mb-2">{icon} — {label}</label>
                      <select
                        value={wcForm[key]}
                        onChange={e => setWcForm(prev => ({ ...prev, [key]: e.target.value }))}
                        data-testid={`wc-admin-${key}`}
                        className="w-full px-4 py-3 bg-background border border-border focus:border-primary rounded-sm text-white outline-none"
                      >
                        <option value="">Select team</option>
                        {filteredTeams.map(team => (
                          <option
                            key={team.id}
                            value={team.id}
                            disabled={selectedIds.includes(team.id) && wcForm[key] !== team.id}
                          >
                            {team.name}
                          </option>
                        ))}
                      </select>
                      {wcForm[key] && (
                        <div className="mt-2 flex items-center gap-2">
                          {wcTeams.find(t => t.id === wcForm[key])?.flag && (
                            <img src={wcTeams.find(t => t.id === wcForm[key]).flag} alt="" className="w-8 h-5 object-cover rounded-sm" />
                          )}
                          <span className="text-white text-sm font-medium">{wcTeams.find(t => t.id === wcForm[key])?.name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Total Goals */}
              <div className="bg-background-subtle border border-border rounded-sm p-4 mb-6">
                <label className="block text-sm uppercase tracking-wide text-[#FFD700] font-bold mb-2">Total Goals in Tournament</label>
                <input
                  type="number"
                  min={0}
                  max={500}
                  value={wcForm.total_goals}
                  onChange={e => setWcForm(prev => ({ ...prev, total_goals: parseInt(e.target.value) || 0 }))}
                  className="w-32 px-4 py-3 bg-background border border-border rounded-sm text-white font-mono text-xl text-center focus:border-primary outline-none"
                  data-testid="wc-admin-total-goals"
                />
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveWcResults}
                data-testid="wc-admin-save-results"
                className="px-8 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all hover:scale-105 glow-effect"
              >
                Save World Cup Results
              </button>

              {wcStatus?.results_entered && (
                <p className="mt-4 text-primary text-sm font-mono">Results have been entered. Leaderboard is live.</p>
              )}
            </div>
          </TabsContent>

          {/* WC Matches Tab */}
          <TabsContent value="wcmatches" data-testid="wcmatches-section">
            <div className="bg-background-paper border border-border rounded-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Skull size={28} weight="bold" className="text-accent" />
                  <h2 className="font-heading text-2xl font-bold text-white">WC 2026 Match Schedule</h2>
                </div>
                <div className="flex items-center gap-3">
                  {syncStatus?.last_sync && (
                    <span className="text-text-muted text-xs hidden sm:inline">
                      {t('admin.lastSync')}: {new Date(syncStatus.last_sync).toLocaleTimeString()}
                    </span>
                  )}
                  <span className="text-xs text-green-400/70 hidden sm:inline">{t('admin.autoSync')}</span>
                  <button
                    onClick={handleSyncResults}
                    disabled={syncing}
                    data-testid="sync-results-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold text-sm uppercase tracking-wide rounded-sm transition-all"
                  >
                    <ArrowsClockwise size={18} weight="bold" className={syncing ? 'animate-spin' : ''} />
                    <span>{syncing ? t('admin.syncing') : t('admin.syncResults')}</span>
                  </button>
                </div>
              </div>
              <p className="text-text-secondary text-sm mb-6">
                Add World Cup matches for the Last Man Standing competition. Users pick one winning team per match day.
              </p>

              {/* Add Match Form */}
              <div className="bg-background-subtle border border-border rounded-sm p-4 mb-6">
                <h3 className="text-sm uppercase tracking-wide text-[#FFD700] font-bold mb-3">Add New Match</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <select
                    value={wcMatchForm.home_team_id}
                    onChange={e => setWcMatchForm(p => ({ ...p, home_team_id: e.target.value }))}
                    data-testid="wc-match-home-team"
                    className="px-3 py-2 bg-background border border-border focus:border-primary rounded-sm text-white text-sm outline-none"
                  >
                    <option value="">Home Team</option>
                    {wcTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <select
                    value={wcMatchForm.away_team_id}
                    onChange={e => setWcMatchForm(p => ({ ...p, away_team_id: e.target.value }))}
                    data-testid="wc-match-away-team"
                    className="px-3 py-2 bg-background border border-border focus:border-primary rounded-sm text-white text-sm outline-none"
                  >
                    <option value="">Away Team</option>
                    {wcTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <input
                    type="date"
                    value={wcMatchForm.match_date}
                    onChange={e => setWcMatchForm(p => ({ ...p, match_date: e.target.value }))}
                    data-testid="wc-match-date"
                    className="px-3 py-2 bg-background border border-border focus:border-primary rounded-sm text-white text-sm outline-none"
                  />
                  <input
                    type="time"
                    value={wcMatchForm.match_time}
                    onChange={e => setWcMatchForm(p => ({ ...p, match_time: e.target.value }))}
                    data-testid="wc-match-time"
                    className="px-3 py-2 bg-background border border-border focus:border-primary rounded-sm text-white text-sm outline-none"
                  />
                  <select
                    value={wcMatchForm.stage}
                    onChange={e => setWcMatchForm(p => ({ ...p, stage: e.target.value }))}
                    data-testid="wc-match-stage"
                    className="px-3 py-2 bg-background border border-border focus:border-primary rounded-sm text-white text-sm outline-none"
                  >
                    <option>Group</option>
                    <option>Round of 32</option>
                    <option>Round of 16</option>
                    <option>Quarter-final</option>
                    <option>Semi-final</option>
                    <option>Third-place</option>
                    <option>Final</option>
                  </select>
                  <input
                    type="text"
                    value={wcMatchForm.group}
                    onChange={e => setWcMatchForm(p => ({ ...p, group: e.target.value }))}
                    placeholder="Group (e.g. A)"
                    data-testid="wc-match-group"
                    className="px-3 py-2 bg-background border border-border focus:border-primary rounded-sm text-white text-sm placeholder:text-text-muted outline-none"
                  />
                </div>
                <button
                  onClick={handleCreateWcMatch}
                  data-testid="wc-add-match-btn"
                  className="mt-3 px-6 py-2 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide text-sm rounded-sm transition-all"
                >
                  Add Match
                </button>
              </div>

              {/* Match List */}
              <div className="space-y-2">
                {wcMatches.length === 0 ? (
                  <p className="text-text-secondary text-center py-8">No WC matches added yet</p>
                ) : (
                  wcMatches.map(match => (
                    <div key={match.id} className="bg-background-subtle border border-border rounded-sm p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <span className="text-xs font-mono text-text-muted w-24">{match.match_date}</span>
                          <span className="text-xs text-text-muted w-16">{match.match_time} UTC</span>
                          <span className="text-xs text-[#FFD700] w-20">{match.stage}</span>
                          <div className="flex items-center gap-2">
                            {match.home_team?.flag && <img src={match.home_team.flag} alt="" className="w-5 h-3 object-cover rounded-sm" />}
                            <span className="text-white text-sm">{match.home_team?.name || '?'}</span>
                          </div>
                          {match.status === 'completed' ? (
                            <span className="font-mono text-white font-bold">{match.home_score} - {match.away_score}</span>
                          ) : (
                            <span className="text-text-muted text-xs">vs</span>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm">{match.away_team?.name || '?'}</span>
                            {match.away_team?.flag && <img src={match.away_team.flag} alt="" className="w-5 h-3 object-cover rounded-sm" />}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {match.status !== 'completed' && (
                            <button
                              onClick={() => { setWcResultMatch(match); setWcMatchHomeScore(0); setWcMatchAwayScore(0); setWcMatchWinner(''); }}
                              data-testid={`wc-enter-result-${match.id}`}
                              className="px-3 py-1 bg-[#FFD700]/20 text-[#FFD700] text-xs font-bold rounded-sm hover:bg-[#FFD700]/30"
                            >
                              Result
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteWcMatch(match.id)}
                            data-testid={`wc-delete-match-${match.id}`}
                            className="p-1 text-accent hover:text-accent/80"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Leagues Tab */}
          <TabsContent value="leagues" data-testid="admin-leagues-section">
            <div className="bg-background-paper border border-border rounded-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold text-white flex items-center space-x-2">
                  <Users size={24} className="text-primary" />
                  <span>{t('admin.allLeagues')}</span>
                </h2>
                <span className="text-text-muted text-sm">{adminLeagues.length} {t('admin.totalLeagues')}</span>
              </div>

              {adminLeagues.length === 0 ? (
                <p className="text-text-muted text-center py-8">{t('admin.noLeagues')}</p>
              ) : (
                <div className="space-y-3">
                  {adminLeagues.map((league) => (
                    <div key={league.id} className="border border-border rounded-sm overflow-hidden" data-testid={`admin-league-${league.id}`}>
                      <div
                        className="flex items-center justify-between p-4 bg-background-subtle hover:bg-background-subtle/80 cursor-pointer transition-all"
                        onClick={() => setExpandedLeague(expandedLeague === league.id ? null : league.id)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <p className="font-heading text-lg font-bold text-white">{league.name}</p>
                            <span className="font-mono text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">{league.code}</span>
                          </div>
                          <p className="text-text-muted text-xs">
                            {t('admin.createdBy')}: {league.creator_email} · {league.members.length} {t('leagues.members')}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteLeague(league.id, league.name); }}
                            data-testid={`delete-league-${league.id}`}
                            className="p-2 text-red-400 hover:bg-red-400/10 rounded transition-all"
                          >
                            <Trash size={18} />
                          </button>
                          {expandedLeague === league.id ? <CaretUp size={20} className="text-text-muted" /> : <CaretDown size={20} className="text-text-muted" />}
                        </div>
                      </div>

                      {expandedLeague === league.id && (
                        <div className="border-t border-border p-4 bg-background">
                          <p className="text-text-muted text-xs uppercase tracking-wide mb-3">{t('admin.membersList')}</p>
                          <div className="space-y-2">
                            {league.members.map((member) => (
                              <div key={member.id} className="flex items-center justify-between p-3 bg-background-subtle rounded-sm" data-testid={`admin-member-${member.id}`}>
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                    <span className="text-primary text-xs font-bold">{member.display_name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase() || '?'}</span>
                                  </div>
                                  <div>
                                    <p className="text-white text-sm font-medium">{member.display_name || member.email}</p>
                                    <p className="text-text-muted text-xs">{member.email}</p>
                                    {member.id === league.creator_id && (
                                      <span className="text-primary text-xs">{t('admin.creator')}</span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveMember(league.id, member.id, member.display_name || member.email)}
                                  data-testid={`remove-member-${member.id}`}
                                  className="p-1.5 text-red-400 hover:bg-red-400/10 rounded transition-all text-xs flex items-center space-x-1"
                                >
                                  <Trash size={14} />
                                  <span>{t('admin.remove')}</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="team-modal">
          <div className="bg-background-paper border border-border rounded-sm p-8 max-w-md w-full mx-4">
            <h3 className="font-heading text-2xl font-bold text-white mb-6">{t('admin.addTeam')}</h3>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              data-testid="team-name-input"
              placeholder={t('admin.teamName')}
              className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all mb-4"
            />
            <input
              type="text"
              value={teamLogo}
              onChange={(e) => setTeamLogo(e.target.value)}
              data-testid="team-logo-input"
              placeholder={t('admin.teamLogo')}
              className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all mb-4"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => setShowTeamModal(false)}
                data-testid="cancel-team-button"
                className="flex-1 py-3 bg-transparent border border-border text-text-secondary hover:border-primary hover:text-primary rounded-sm transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateTeam}
                data-testid="save-team-button"
                className="flex-1 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Match Modal */}
      {showMatchModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="match-modal">
          <div className="bg-background-paper border border-border rounded-sm p-8 max-w-md w-full mx-4">
            <h3 className="font-heading text-2xl font-bold text-white mb-6">{t('admin.addMatch')}</h3>
            <div className="space-y-4">
              <select
                value={homeTeamId}
                onChange={(e) => setHomeTeamId(e.target.value)}
                data-testid="home-team-select"
                className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white outline-none transition-all"
              >
                <option value="">{t('admin.homeTeam')}</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
              <select
                value={awayTeamId}
                onChange={(e) => setAwayTeamId(e.target.value)}
                data-testid="away-team-select"
                className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white outline-none transition-all"
              >
                <option value="">{t('admin.awayTeam')}</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
              <input
                type="number"
                value={week}
                onChange={(e) => setWeek(e.target.value)}
                data-testid="week-input"
                placeholder="Week"
                min="1"
                className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all"
              />
              <input
                type="datetime-local"
                value={matchDate}
                onChange={(e) => setMatchDate(e.target.value)}
                data-testid="match-date-input"
                className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white outline-none transition-all"
              />
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowMatchModal(false)}
                data-testid="cancel-match-button"
                className="flex-1 py-3 bg-transparent border border-border text-text-secondary hover:border-primary hover:text-primary rounded-sm transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateMatch}
                data-testid="save-match-button"
                className="flex-1 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showResultModal && selectedMatch && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="result-modal">
          <div className="bg-background-paper border border-border rounded-sm p-8 max-w-md w-full mx-4">
            <h3 className="font-heading text-2xl font-bold text-white mb-6">{t('admin.updateResult')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">{getTeamName(selectedMatch.home_team_id)}</label>
                <input
                  type="number"
                  value={homeScore}
                  onChange={(e) => setHomeScore(e.target.value)}
                  data-testid="home-score-input"
                  min="0"
                  className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">{getTeamName(selectedMatch.away_team_id)}</label>
                <input
                  type="number"
                  value={awayScore}
                  onChange={(e) => setAwayScore(e.target.value)}
                  data-testid="away-score-input"
                  min="0"
                  className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white outline-none transition-all"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowResultModal(false)}
                data-testid="cancel-result-button"
                className="flex-1 py-3 bg-transparent border border-border text-text-secondary hover:border-primary hover:text-primary rounded-sm transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleUpdateResult}
                data-testid="save-result-button"
                className="flex-1 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WC Match Result Modal */}
      {wcResultMatch && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="wc-result-modal">
          <div className="bg-background-paper border border-border rounded-sm p-8 max-w-md w-full mx-4">
            <h3 className="font-heading text-2xl font-bold text-white mb-2">Enter Match Result</h3>
            <p className="text-text-secondary text-sm mb-6">
              {wcResultMatch.home_team?.name} vs {wcResultMatch.away_team?.name}
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">{wcResultMatch.home_team?.name}</label>
                  <input
                    type="number"
                    min="0"
                    value={wcMatchHomeScore}
                    onChange={e => setWcMatchHomeScore(parseInt(e.target.value) || 0)}
                    data-testid="wc-result-home-score"
                    className="w-full px-4 py-3 bg-background border border-border focus:border-primary rounded-sm text-white text-center font-mono text-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">{wcResultMatch.away_team?.name}</label>
                  <input
                    type="number"
                    min="0"
                    value={wcMatchAwayScore}
                    onChange={e => setWcMatchAwayScore(parseInt(e.target.value) || 0)}
                    data-testid="wc-result-away-score"
                    className="w-full px-4 py-3 bg-background border border-border focus:border-primary rounded-sm text-white text-center font-mono text-xl outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Winner (leave empty for draw)</label>
                <select
                  value={wcMatchWinner}
                  onChange={e => setWcMatchWinner(e.target.value)}
                  data-testid="wc-result-winner"
                  className="w-full px-4 py-3 bg-background border border-border focus:border-primary rounded-sm text-white outline-none"
                >
                  <option value="">Draw (no winner)</option>
                  <option value={wcResultMatch.home_team_id}>{wcResultMatch.home_team?.name}</option>
                  <option value={wcResultMatch.away_team_id}>{wcResultMatch.away_team?.name}</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setWcResultMatch(null)}
                className="flex-1 py-3 bg-transparent border border-border text-text-secondary hover:border-primary hover:text-primary rounded-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSetWcMatchResult(wcResultMatch.id)}
                data-testid="wc-save-result-btn"
                className="flex-1 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all"
              >
                Save Result
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
